<template>
    <div>
        <!-- Header -->
        <div class="row p-3">
            <div class="col-8">
                <h4 id="calendar-heading" style="color: #0b4b8f">
                    {{ currentMonthName }} {{ currentYear }}
                </h4>
            </div>
            <div class="col-4 d-flex justify-content-end">
                <div>
                    <button
                        @click="previousMonth"
                        class="month-btn"
                    >
                        <font-awesome-icon icon="arrow-left"/>
                    </button>
                    <button
                        @click="nextMonth"
                        class="month-btn"
                    >
                        <font-awesome-icon icon="arrow-right"/>
                    </button>
                </div>
            </div>
        </div>

        <!-- Calendar Grid -->
        <div class="col-12 px-3">
            <!-- Weekday headers -->
            <div class="row pb-2 mb-2" style="border-bottom: 1px solid #e3e3e3">
                <div
                    v-for="(day, index) in WEEKDAYS"
                    :key="day"
                    role="columnheader"
                    class="col text-bold text-center"
                    :aria-label="WEEKDAYS_FULL[index]"
                >
                    {{ day }}
                </div>
            </div>

            <!-- Days grid -->
            <div class="col-12">
                <div class="row" v-for="(week, wi) in weeks" :key="wi">
                    <div
                        v-for="(day, index) in week"
                        :key="index"
                        class="col"
                    >
                        <button
                            v-if="day !== null"
                            @click="!isFuture(day) && selectDate(day)"
                            :disabled="isFuture(day)"
                            :class="getDayClasses(day)"
                            :aria-label="getDayAriaLabel(day)"
                            :aria-pressed="isSelected(day)"
                            :aria-disabled="isFuture(day)"
                        >
                            <span class="text-sm">{{ day }}</span>
                            <div :class="getExportClass(day)" :title="$t('contract_diary.exported')"/>
                        </button>
                        <div v-else class="w-full h-full" aria-hidden="true"></div>
                    </div>

                </div>
            </div>
        </div>

        <!-- Current month summary -->
        <div class="mt-2 p-3 border-t border-gray-200">
            <span class="text-sm text-gray-600" role="status" aria-live="polite">
                {{ entriesInCurrentMonth }} {{ $t('contract_diary.calendar_count')}}
            </span>
        </div>
    </div>
</template>

<script>

import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
export default {
    name: 'ContractDiaryCalendar',
    components: {FontAwesomeIcon},
    props: {
        diaries: {
            type: Array,
            default: []
        }
    },
    data: function () {
        return {
            currentDate: new Date(),
            selectedDay: null,
            WEEKDAYS: ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'],
            WEEKDAYS_FULL: [
                'Maanantai',
                'Tiistai',
                'Keskiviikko',
                'Torstai',
                'Perjantai',
                'Lauantai',
                'Sunnuntai',
            ],
            MONTHS: [
                'Tammikuu',
                'Helmikuu',
                'Maaliskuu',
                'Huhtikuu',
                'Toukokuu',
                'Kesäkuu',
                'Heinäkuu',
                'Elokuu',
                'Syyskuu',
                'Lokakuu',
                'Marraskuu',
                'Joulukuu',
            ],
        }
    },
    computed: {
        currentYear() {
            return this.currentDate?.getFullYear()
        },
        currentMonth() {
            return this.currentDate?.getMonth()
        },
        currentMonthName() {
            return this.MONTHS[this.currentMonth]
        },
        firstDayOfMonth() {
            return new Date(this.currentYear, this.currentMonth, 1)
        },
        lastDayOfMonth() {
            return new Date(this.currentYear, this.currentMonth + 1, 0)
        },
        startingDayOfWeek() {
            let day = this.firstDayOfMonth.getDay();
            return day === 0 ? 6 : day - 1;
        },
        daysInMonth() {
            return this.lastDayOfMonth.getDate()
        },
        calendarDays() {
            const days = [];
            // Add empty days at the beginning
            for (let i = 0; i < this.startingDayOfWeek; i++) {
                days.push(null);
            }
            // Add days of the month
            for (let day = 1; day <= this.daysInMonth; day++) {
                days.push(day);
            }
            // Add empty days at the end
            let count = days.length
            while (count % 7 !== 0) {
                days.push(null);
                count++
            }
            return days;
        },
        weeks() {
            const weeks = []
            const calDays = this.calendarDays
            let idx = 0
            while (idx <= calDays.length) {
                weeks.push(calDays.slice(idx, idx + 7))
                idx += 7
            }
            return weeks
        },
        entriesInCurrentMonth() {
            return this.diaries.length
        }
    },
    watch: {
        contract() {
           this.selectedDay = null
        },
        diaries() {
            if (this.selectedDay) {
                // Emit selected day after data set change
                this.$emit('dateSelect', this.selectedDay);
            }
        }
    },
    methods: {
        previousMonth() {
            this.currentDate = new Date(this.currentYear, this.currentMonth - 1, 1);
            this.selectedDay = null
            this.$nextTick(() => this.$emit('monthChanged', this.getCurrentMonthStart()))
        },

        nextMonth() {
            this.currentDate = new Date(this.currentYear, this.currentMonth + 1, 1);
            this.selectedDay = null
            this.$nextTick(() => this.$emit('monthChanged', this.getCurrentMonthStart()))
        },

        selectDate(day) {
            this.selectedDay = new Date(this.currentYear, this.currentMonth, day);
            this.$emit('dateSelect', this.selectedDay);
        },

        hasEntry(day) {
            return this.diaries.some(
                (entry) => {
                    const entryDate = new Date(entry.record_date)
                    return entryDate.getDate() === day &&
                        entryDate.getMonth() === this.currentMonth &&
                        entryDate.getFullYear() === this.currentYear
                }
            )
        },

        isExported(day) {
            return this.diaries.some(
                (entry) => {
                    const entryDate = new Date(entry.record_date)
                    return entryDate.getDate() === day &&
                        entryDate.getMonth() === this.currentMonth &&
                        entryDate.getFullYear() === this.currentYear &&
                        entry.exported_to_external_system
                }
            )
        },

        isToday(day) {
            const today = new Date();
            return (
                day === today.getDate() &&
                this.currentMonth === today.getMonth() &&
                this.currentYear === today.getFullYear()
            )
        },

        isFuture(day) {
            const date = new Date(this.currentYear, this.currentMonth, day);
            const today = new Date();
            const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            return date > todayStart;
        },

        isSelected(day) {
            if (!this.selectedDay) return false;
            return (
                day === this.selectedDay.getDate() &&
                this.currentMonth === this.selectedDay.getMonth() &&
                this.currentYear === this.selectedDay.getFullYear()
            )
        },

        getExportClass(day) {
            const baseClasses = []
            if (this.isExported(day)) {
                baseClasses.push('status-dot');
            }
            else {
                baseClasses.push('hidden')
            }
            return baseClasses
        },

        getDayClasses(day) {
            const baseClasses = [
                'day-class'
            ];

            // Handle future days
            if (this.isFuture(day)) {
                baseClasses.push('opacity-40', 'cursor-not-allowed', 'border-gray-200', 'bg-gray-50');
                return baseClasses.join(' ');
            } else {
                baseClasses.push('active-day-class');
            }

            // Add border colors based on entry status and today
            if (this.isToday(day)) {
                baseClasses.push('today-class');
            }

            // Add green background if entry exists
            if (this.hasEntry(day)) {
                baseClasses.push('diary-day-class');
            }

            if (this.isSelected(day)) {
                baseClasses.push('selected-day-class');
            }

            return baseClasses.join(' ');
        },

        getDayAriaLabel(day) {
            if (this.isFuture(day)) {
                return `${day}. ${this.currentMonthName} ${this.currentYear}, tuleva päivä, ei saatavilla`;
            }
            const hasEntryText = this.hasEntry(day) ? ', työmaapäiväkirja tehty' : ', ei työmaapäiväkirjaa';
            return `${day}. ${this.currentMonthName} ${this.currentYear}${hasEntryText}`;
        },

        getSelectedDay() {
            return this.selectedDay
        },

        getCurrentMonthStart() {
            let date = new Date();
            date.setDate(1)
            date.setMonth(this.currentMonth)
            date.setFullYear(this.currentYear);
            date.setHours(0, 0, 0, 0);
            return date
        }

    }

}

// Methods

</script>

<style scoped>
.month-btn {
    display: inline-block;
    margin-left: .5em;
    background-color: #FFFFFF;
    color: #A4D300;
    width: 2em;
    text-align: center;
    padding: .3em;
    border-radius: .5em;
    border: 1px solid #BCBCBC;
}

.month-btn:hover {
    background-color: #f8f8f8;
}

.day-class, .diary-day-class {
    position: relative;
    aspect-ratio: 1 / 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    border-radius: .5em;
}

.today-class {
    color: #0b4b8f;
    text-decoration: underline;
    background-color: #FFFFFF;
    font-weight: bold;
    border: 1px solid #0b4b8f;
}

.status-dot {
    position: absolute;
    right: 4px;
    bottom: 4px;
    width: 6px;
    height: 6px;
    background: #5f8f01;
    border-radius: 50%;
}

.diary-day-class {
    border: 1px solid #ddd;
    background-color: #e3ecc4;
}

.selected-day-class, .diary-day-class:hover {
    border: 2px solid #A4D300
}

.future-day {
    background-color: #eeeded;
}

</style>
